// Clases Tailwind compartidas por los componentes de presentacion y por las estrategias
// de pasarela. Se usan valores arbitrarios sobre las CSS variables var(--apipay-*) que
// applyTenantTheme() escribe en :root tras validarlas por regex (plan 7.3.5), en vez de
// tokens del tailwind.config: asi el widget conserva su aspecto aunque el CSS de entrada
// no cargue el config con @config, y el tema del tenant sigue mandando.

export const STACK_CLASS = 'flex flex-col gap-3';

/*
 * Tarjeta ELEVADA en vez de un rectangulo con borde de 1px.
 *
 * El borde plano sobre fondo plano es lo que hacia que el checkout se viera apagado: sin elevacion
 * no hay jerarquia, y todo —resumen, selector, avisos— pesaba igual. Se cambia el borde por un
 * `ring` de muy bajo contraste mas una sombra suave: la tarjeta se despega del fondo del modo pagina
 * y sigue siendo correcta sobre el fondo del comercio en modo embebido, donde la sombra apenas se
 * percibe.
 *
 * El radio tiene suelo de 14px cuando el tenant no fija --apipay-radius; si lo fija, manda el suyo.
 */
export const SURFACE_CLASS =
  'rounded-[var(--apipay-radius,14px)] ring-1 ring-black/[0.06] ' +
  'bg-[var(--apipay-color-surface,#ffffff)] p-5 text-[var(--apipay-color-text,#0f172a)] ' +
  'shadow-[var(--apipay-shadow-card)]';

export const TITLE_CLASS =
  'text-[15px] font-semibold tracking-[-0.01em] text-[var(--apipay-color-text,#0f172a)]';

export const MUTED_TEXT_CLASS = 'text-sm text-[var(--apipay-color-muted,#64748b)]';

/** El importe es el dato que el comprador viene a verificar: manda en la jerarquia tipografica. */
export const AMOUNT_CLASS =
  'text-[2rem] font-bold leading-none tracking-[-0.02em] tabular-nums ' +
  'text-[var(--apipay-color-text,#0f172a)]';

/**
 * Panel del importe: fondo teñido con el color del comercio.
 *
 * Es el unico bloque del checkout que lleva el color de marca ademas del boton, y a proposito: guia
 * la vista al total antes de bajar al selector.
 */
export const AMOUNT_PANEL_CLASS =
  'rounded-[calc(var(--apipay-radius,14px)-2px)] bg-[var(--apipay-primary-050,#f5f8ff)] ' +
  'px-4 py-3 ring-1 ring-[var(--apipay-primary-200,#c7d7fe)]/60';

export const PRIMARY_BUTTON_CLASS =
  'inline-flex w-full items-center justify-center rounded-[var(--apipay-radius,14px)] ' +
  'bg-[var(--apipay-color-primary,#3a4fd9)] px-4 py-3.5 text-[15px] font-semibold text-white ' +
  'shadow-[var(--apipay-shadow-cta)] transition-[transform,filter,box-shadow] ' +
  'hover:brightness-110 active:translate-y-px active:brightness-95 ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none disabled:hover:brightness-100';

export const SECONDARY_BUTTON_CLASS =
  'inline-flex w-full items-center justify-center rounded-[var(--apipay-radius,12px)] ' +
  'border border-[var(--apipay-color-border,#e2e8f0)] px-4 py-3 text-sm font-medium ' +
  'text-[var(--apipay-color-text,#0f172a)] transition-colors hover:bg-black/5 ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

export const OPTION_BUTTON_CLASS =
  'flex w-full items-center justify-between gap-3 rounded-[var(--apipay-radius,12px)] ' +
  'border border-[var(--apipay-color-border,#e2e8f0)] px-4 py-3 text-left text-sm font-medium ' +
  'text-[var(--apipay-color-text,#0f172a)] transition-colors hover:border-[var(--apipay-color-primary,#3a4fd9)] ' +
  'hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';

export const ALERT_CLASS =
  'rounded-[var(--apipay-radius,12px)] border border-[#fca5a5] bg-[#fef2f2] p-3 text-sm text-[#991b1b]';

/**
 * Segunda linea del aviso de error: el `detail` que mando el servidor (ver ErrorMessage).
 *
 * Se separa con una regla y baja a text-xs con opacidad para que quede legible pero SUBORDINADO: la
 * frase que le habla al comprador manda, y esta es la que permite al comercio arreglar la causa.
 * `break-words` porque el texto lo redacta el servidor y puede traer una cadena larga sin espacios
 * —un id, una ruta de campo— que de otro modo desbordaria la tarjeta en un movil.
 */
export const ALERT_DETAIL_CLASS =
  'mt-2 border-t border-[#fca5a5] pt-2 text-xs break-words opacity-80';

export const NOTICE_CLASS =
  'rounded-[var(--apipay-radius,12px)] border border-[var(--apipay-color-border,#e2e8f0)] ' +
  'bg-black/[0.02] p-3 text-xs leading-relaxed text-[var(--apipay-color-muted,#64748b)]';

/*
 * EL RECUADRO Y EL PUNTO DE MONTAJE SON DOS ELEMENTOS, y tienen que seguir siendolo.
 *
 * Medido en QA el 2026-09-17 con el formulario de BCI montado: su SDK dimensiona NUESTRO propio
 * elemento --el que recibe por selector en `showEmbeddedPage`-- a la altura de su iframe y le pone
 * `overflow: hidden`. Sobre el contenedor de entonces, que llevaba `p-3` y un borde de 1px, salia
 * `height: 646px` con `scrollHeight` 670: 26 px recortados, que son exactamente 12 + 12 + 1 + 1, o
 * sea el padding y el borde. Y lo que vive en esa franja de abajo es el boton de pagar, asi que el
 * comprador no podia pagar.
 *
 * El reparto es por eso: el adorno --borde, radio, separacion-- va en el ENVOLTORIO, cuya altura la
 * sigue mandando su contenido, y dentro va el elemento DESNUDO que el SDK dimensiona. El recuadro se
 * ve igual que antes; lo que cambia es quien lo lleva.
 */
export const HOSTED_FRAME_CLASS =
  'min-h-[3.5rem] rounded-[var(--apipay-radius,12px)] border border-[var(--apipay-color-border,#e2e8f0)] p-3';

/**
 * Contenedor vacio donde la pasarela monta su propio iframe alojado (nunca inputs nuestros).
 *
 * <p><strong>SIN CAJA PROPIA: ni padding, ni borde.</strong> No es una preferencia estetica --es la
 * condicion que impone un SDK que fija la altura de este elemento y le recorta lo que sobresalga.
 * Lo sujeta {@code montajeAlojadoSinCaja.test.tsx}; el adorno va en {@link HOSTED_FRAME_CLASS}.
 */
export const HOSTED_MOUNT_CLASS = 'block w-full';

export const STATUS_ROW_CLASS = 'flex items-center gap-2 text-sm text-[var(--apipay-color-muted,#64748b)]';

export const TABLE_CLASS = 'w-full border-collapse text-left text-xs';

export const TABLE_CELL_CLASS =
  'border-b border-[var(--apipay-color-border,#e2e8f0)] px-2 py-1.5 align-top';

/**
 * Titulo de una pantalla COMPLETA —espera o desenlace—, un escalon por encima del de una tarjeta.
 *
 * Existe porque {@link TITLE_CLASS} (15px) es el titulo de un bloque dentro del checkout, y usarlo
 * tambien para «Pago realizado» dejaba el desenlace pesando lo mismo que el resumen del pedido. En
 * una pantalla que solo tiene una cosa que decir, esa cosa tiene que mandar.
 */
export const SCREEN_TITLE_CLASS =
  'text-[19px] font-semibold leading-tight tracking-[-0.01em] text-[var(--apipay-color-text,#0f172a)]';

/** El importe del recibo: mismo peso que el del resumen, porque es el mismo dato. */
export const STATUS_AMOUNT_CLASS =
  'text-[1.75rem] font-bold leading-none tracking-[-0.02em] tabular-nums ' +
  'text-[var(--apipay-color-text,#0f172a)]';

/**
 * La referencia del cobro. En monoespaciada y pequena: no compite con el importe, pero se puede
 * leer digito a digito por telefono, que es justo para lo que sirve.
 */
export const STATUS_REFERENCE_CLASS =
  'font-mono text-xs text-[var(--apipay-color-muted,#64748b)] break-all';

/**
 * Disco del icono terminal, tenido segun el desenlace.
 *
 * <p>Los colores de estado NO salen del tema del comercio, y es deliberado: un rechazo tiene que
 * verse rojo aunque la marca del comercio sea roja, y un exito verde aunque su primario sea verde.
 * El tema manda en el resto del checkout —boton, realces, radio—; aqui manda el significado.
 *
 * <p>El disco mide 64 px frente a los 40 px del icono suelto que habia antes. No es tamano por
 * tamano: un icono sin fondo flotando sobre la tarjeta no tiene donde apoyarse, y era lo que hacia
 * que la pantalla se viera sin terminar.
 */
export function statusBadgeClass(
  status: 'success' | 'failed' | 'expired' | 'reversed' | 'blocked',
): string {
  const TONO: Record<typeof status, string> = {
    success: 'bg-[#e8f6ee] text-[#12813f] ring-[#12813f]/15',
    failed: 'bg-[#fdecec] text-[#b91c1c] ring-[#b91c1c]/15',
    expired: 'bg-[#fdf3e3] text-[#b45309] ring-[#b45309]/15',
    // El mismo ambar que `expired` y NO el rojo del rechazo: una venta anulada no es un fallo del
    // comprador ni de su medio de pago, es una decision del comercio. Rojo aqui le haria pensar
    // que algo se le rompio.
    reversed: 'bg-[#fdf3e3] text-[#b45309] ring-[#b45309]/15',
    blocked: 'bg-[#eef0f3] text-[#475569] ring-[#475569]/15',
  };
  /*
   * `apipay-sin-anclaje` no pinta nada aqui, y va igualmente.
   *
   * La animacion de entrada de este disco acaba en `transform: scale(1)` con `both`, o sea que el
   * elemento RETIENE un transform para siempre --`scale(1)` no es `none`-- y eso lo convierte en
   * bloque contenedor de cualquier `position: fixed` que cuelgue de el. Hoy no cuelga ninguno: esto
   * es una pantalla terminal y la pasarela ya se desmonto. Pero la regla del widget es «lo que
   * ancla, lleva la marca», sin excepciones de las que haya que acordarse: asi el dia que algo fijo
   * entre aqui debajo no hay que volver a descubrir por que no se pega a la ventana. El caso que lo
   * enseno, con las medidas, esta en styles.css.
   */
  return (
    'apipay-badge-in apipay-sin-anclaje grid h-16 w-16 place-items-center rounded-full ring-1 ' +
    TONO[status]
  );
}
